import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Building2, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("sen6135@gmail.com");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Giriş başarılı. Hoş geldiniz!");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B0F17]">
      {/* Visual side */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 overflow-hidden login-visual">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/12821597/pexels-photo-12821597.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0F17]/95 via-[#0B0F17]/80 to-blue-950/70" />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/40">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading font-extrabold text-2xl tracking-tight text-white">VİLAYET</span>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight">
            Tüm giderleriniz,
            <br />
            <span className="text-blue-400">tek pencerede.</span>
          </h1>
          <p className="mt-5 text-slate-300 text-base max-w-md leading-relaxed">
            Personel maaşları, faturalar ve şube takibi. Ödeme günü yaklaşan her fatura için anında hatırlatma.
          </p>
          <div className="mt-8 flex items-center gap-2 text-slate-400 text-sm">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            Güvenli muhasebe & şube yönetim sistemi
          </div>
        </div>
        <div className="relative text-slate-500 text-xs">© 2026 VİLAYET Muhasebe Sistemi</div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-extrabold text-2xl text-white">VİLAYET</span>
          </div>

          <h2 className="font-heading text-3xl font-bold text-white tracking-tight">Giriş Yap</h2>
          <p className="text-slate-400 mt-2 text-sm">Hesabınıza erişmek için bilgilerinizi girin.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-semibold text-slate-400">E-posta</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 w-full bg-slate-900/80 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                placeholder="ornek@vilayet.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-semibold text-slate-400">Şifre</label>
              <div className="relative mt-2">
                <input
                  data-testid="login-password-input"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-4 py-3 pr-11 text-white text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 shadow-lg shadow-blue-600/25"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Giriş yapılıyor…" : "Giriş Yap"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <a href="/personel-giris" data-testid="personnel-login-link" className="text-xs text-emerald-400 hover:text-emerald-300">
              Personel girişi için tıklayın →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
